import { ENDPOINTS, APP_CONFIG } from '../constants';
import { sanitizeInput } from './security';
import type { AssessmentResult } from '../types';

export interface RedTeamRequest {
  prompt: string;
  persona: string;
  targetUrl: string;
  targetModel: string;
}

export const createRedTeamRequest = (
  basePrompt: string,
  selectedPersona: string,
  targetUrl: string,
  targetModel: string
): RedTeamRequest => ({
  prompt: sanitizeInput(basePrompt),
  persona: sanitizeInput(selectedPersona),
  targetUrl: sanitizeInput(targetUrl),
  targetModel: sanitizeInput(targetModel)
});

export const executeRedTeamAssessment = async (
  request: RedTeamRequest
): Promise<AssessmentResult> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), APP_CONFIG.API_TIMEOUT);

  try {
    const response = await fetch(ENDPOINTS.RED_AGENT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // Use default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    return data as AssessmentResult;
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    
    throw new Error('Unknown error occurred during assessment');
  }
};